import { useState, useContext, useMemo, useEffect } from 'react'
import { UserContext, UserContextType, useUserContext } from '@/UserContext'
import { useGet } from 'restful-react'
import { formatBytes } from '@/utils'
import { persistent } from '@/services/LocalStorage'
import * as Constants from '@/services/Constants'
import AccountNotVerifiedAlert from '@/components/AccountNotVerifiedAlert'
import ElectronIPC from '@/services/ElectronIPC'
import { Trans, useTranslation } from 'react-i18next'
import { AppContext, AppContextType } from '@/AppContext'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import useAnalytics from '@/services/useAnalytics'
import { Button } from '@/UIComponents2/Button'
import EyeClosedIcon from '@/CustomIcons/EyeClosedIcon'
import EyeIcon from '@/CustomIcons/EyeIcon'
import { Alert } from '@/UIComponents2/Alert'
import { Username } from '@/components/Username'
import { CardHeader } from '@/components/SessionSettings/internal/CardHeader'
import UserIcon from '@/CustomIcons/UserIcon'
import { Field } from '@/components/SessionSettings/internal/Field'
import { Separator } from '@/components/SessionSettings/internal/Separator'
import { Card, cardXPad } from '@/components/SessionSettings/internal/Card'
import { cn } from '@/utlis/cn'
import BoostIcon from '@/CustomIcons/BoostIcon'
import InfoIcon from '@/CustomIcons/InfoIcon'
import Tooltip from '@/UIComponents2/Tooltip'
import moment from 'moment'

export function AccountSection() {
    const { t } = useTranslation()

    const { state } = useContext(UserContext) as UserContextType

    const getFilespaceUsage = useGet({ path: '/v1/users/_me', queryParams: { view: 'filespace-usage' } })

    useEffect(() => {
        if (getFilespaceUsage.data) {
            persistent.set(Constants.filespaceusage_cache, formatBytes(getFilespaceUsage.data.fileSpaceUsage))
        }
    }, [getFilespaceUsage.data])

    return (
        <Card className="px-0 md:px-0">
            <div className="flex flex-col gap-4 w-full">
                <div className={cn('flex items-start justify-between', cardXPad)}>
                    <CardHeader icon={UserIcon} title="Account" />
                    {!state.isGuest && (
                        <Button
                            variant="secondary"
                            className="shadow-glow bg-white/5 t-sm px-6 py-[7px]"
                            onClick={() => ElectronIPC.openLinkInBrowser(Constants.links.website_login)}
                        >
                            {t('Edit Profile')}
                        </Button>
                    )}
                </div>

                <Field title={t('User Name')}>
                    <Username isMe />
                </Field>
                <Field title={t('Email')}>
                    <EmailRow />
                </Field>

                <Separator />

                <Field title={t('Storage limit per room')}>
                    {formatBytes(state.profile?.policy?.maxPerRoomFileUploadSizeMB, { multiplier: 1000000 })}
                </Field>
                <Field title={t('Boosts remaining')}>
                    <BoostsRow />
                </Field>
                <Field title={t('Account type')}>
                    <AccountTypeRow />
                </Field>
                {(state.profile?.orgs?.length ?? 0) > 0 && (
                    <Field title={t('Organizations')}>
                        {state.profile?.orgs?.map((org) => org.description).join(', ')}
                    </Field>
                )}

                <FreeTrialBanner />

                <div className={cardXPad}>
                    <AccountNotVerifiedAlert active={!state.profile?.emailVerified} />
                </div>
            </div>
        </Card>
    )
}

function EmailRow() {
    const [userState] = useUserContext()

    const [isVisible, setIsVisible] = useState(false)

    const hiddenEmail = useMemo(() => {
        const email = userState.profile?.email || ''
        const [head, provider] = email.split('@')
        return (head[0] || '') + '***' + '@' + provider
    }, [userState.profile])

    return (
        <>
            {isVisible ? (
                <>
                    {userState.profile?.email}
                    <Button className="ml-3">
                        <EyeIcon onClick={() => setIsVisible(false)} style={{ fontSize: '1.3rem' }} />
                    </Button>
                </>
            ) : (
                <>
                    {hiddenEmail}
                    <Button className="ml-3">
                        <EyeClosedIcon onClick={() => setIsVisible(true)} style={{ fontSize: '1.3rem' }} />
                    </Button>
                </>
            )}
        </>
    )
}

function BoostsRow() {
    const { t } = useTranslation()

    const [userState] = useUserContext()

    const { logUserActivityWithContext } = useAnalytics()

    const boostsMax = userState.profile?.boostsMax ?? 2
    const boostsRemaining = userState.profile?.boostsRemaining ?? 0

    const onInfoClick = () => {
        logUserActivityWithContext(AnalyticsEvents.CLICKED_BOOST_SESSION_INFO_BUTTON, {
            boostsRemaining,
            boostsMax,
        })
        ElectronIPC.openLinkInBrowser(Constants.links.about_boosts_link)
    }

    return (
        <>
            <div className="flex items-center gap-2">
                <BoostIcon style={{ fontSize: '1.3rem' }} />
                <div>
                    {boostsRemaining} <span className="t-base-light text-muse-fg/60">of {boostsMax}</span>
                </div>

                <Tooltip text={t('Boost a session to unlock pro features')} className="h-min">
                    <Button onClick={onInfoClick} className="opacity-50">
                        <InfoIcon style={{ fontSize: '1.3rem' }} />
                    </Button>
                </Tooltip>
            </div>
        </>
    )
}

function AccountTypeRow() {
    const { t } = useTranslation()

    const [userState] = useUserContext()

    const { logUserActivityWithContext } = useAnalytics()

    return (
        <div className="flex items-center gap-4">
            {userState.profile?.policy?._id}
            {!Constants.proPolicies.includes(userState.profile?.policy?._id || '') && (
                <Button
                    variant="link"
                    onClick={() => {
                        logUserActivityWithContext(AnalyticsEvents.VIEWED_UPGRADE_PAGE)
                        ElectronIPC.openLinkInBrowser(Constants.links.muse_pro_plans_link)
                    }}
                >
                    {t('Upgrade plan')}
                </Button>
            )}
        </div>
    )
}

function FreeTrialBanner() {
    const { t } = useTranslation()
    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { state } = useContext(UserContext) as UserContextType
    const profile = state.profile ?? null

    if (profile?.policy?._id != 'pro-trial' || !profile?.freeTrialEndDate) {
        return null
    }
    const daysRemaining = moment(profile.freeTrialEndDate).diff(moment(), 'days')

    const onInfoClick = () => {
        appDispatch({
            type: 'update_app_state',
            value: { showFreeTrialInfoDialog: true },
        })
    }

    return (
        <div className={cardXPad}>
            <Alert variant="solid" level="success" contentClassName="w-full">
                <div className="flex items-center justify-between">
                    <div>
                        {t('You are on a free trial')}{' '}
                        <span className="text-sm font-light">
                            (<Trans i18nKey="You have {{count}} days left" t={t} count={daysRemaining} />)
                        </span>
                    </div>
                    <Button variant="link-dark" onClick={onInfoClick} className="pr-2">
                        {t('Details')}
                    </Button>
                </div>
            </Alert>
        </div>
    )
}
